// ============================================================================
// CHARGE TAX MAPPING TYPES
// ============================================================================
// A CRS charge type (optionally one item) → the tax master it is taxed under,
// effective-dated and append-only (crs-db property.tbl_charge_tax_mapping).
// Rows are never edited: a new mapping closes the open one on its start date,
// and an open mapping can be ended on a date without a successor.

/**
 * A charge type code from the charge-type master (Charge Types screen,
 * pms.tbl_charge_type). Since PMS slice 2 any active type can carry a tax rule,
 * not only Room / Meal plan / Add-on.
 */
export type ChargeType = string;
export type ChargeTaxTreatment = 'TAXABLE' | 'EXEMPT' | 'ZERO_RATED';

/** The types whose rule may narrow to one item: room category, meal plan or add-on. */
export const ITEM_CHARGE_TYPES: ReadonlyArray<string> = ['ROOM', 'MEAL', 'ADDON'];

export const isItemChargeType = (code?: string | null): boolean => !!code && ITEM_CHARGE_TYPES.includes(code);

export const TAX_TREATMENT_OPTIONS: Array<{ value: ChargeTaxTreatment; label: string }> = [
  { value: 'TAXABLE', label: 'Taxable' },
  { value: 'EXEMPT', label: 'Exempt' },
  { value: 'ZERO_RATED', label: 'Zero-rated' },
];

export interface ChargeTaxMapping {
  id: string;
  propertyId: string;
  chargeType: ChargeType;
  /** Room category / meal plan / add-on; null = the property's default for the charge type. */
  itemId?: string | null;
  itemName?: string | null;
  jurisdictionCode: string;
  rateCode: string;
  sacHsnCode?: string | null;
  taxTreatment: ChargeTaxTreatment;
  effectiveFrom: string;
  /** Exclusive end; null = open. */
  effectiveTo?: string | null;
  remarks?: string | null;
  rowVersion: number;
  createdAt?: string | null;
}

export interface ChargeTaxMappingSearchRequest {
  propertyId: string;
  chargeType?: ChargeType;
  /** false (default) = open rows only; true = the full effective-dated history. */
  includeHistory?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CreateChargeTaxMappingRequest {
  propertyId: string;
  chargeType: ChargeType;
  itemId?: string | null;
  /** Defaults server-side to the tenant's TAX_POLICY.tax_jurisdiction_code. */
  jurisdictionCode?: string;
  rateCode: string;
  sacHsnCode?: string | null;
  taxTreatment: ChargeTaxTreatment;
  effectiveFrom: string;
  remarks?: string | null;
}

export interface CloseChargeTaxMappingRequest {
  /** Exclusive end date; must be after the row's effective from. */
  effectiveTo: string;
  rowVersion: number;
  remarks?: string | null;
}
